import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { createContext, useContext, useState } from "react";
import {
  addMemberToGroupChat,
  getGroupChat,
  getGroupChatMembers,
  getGroupChats,
  removeMemberFromGroupChat,
  updateGroupChat,
} from "../api/groupChats.api";
import { RelationshipRow } from "../api/relationship.type";
import { useAuthContext } from "../containers/AuthContextWrapper";
import { useToastContext } from "../containers/ToastContextWrapper";

export interface GroupChatContextType {
  // Group Chat Data
  groupChats: any[];
  selectedGroupChat: any | null;
  groupChatMembers: any[];

  // Loading States
  isLoadingGroupChats: boolean;
  isLoadingGroupChat: boolean;
  isLoadingMembers: boolean;

  // Actions
  selectGroupChat: (groupChatId: string) => void;
  updateGroupChatName: (id: string, name: string) => Promise<void>;
  addMemberToGroupChat: (
    groupChatId: string,
    contact: RelationshipRow
  ) => Promise<void>;
  removeMemberFromGroupChat: (memberId: string) => Promise<void>;

  // Queries
  refetchGroupChats: () => void;
  refetchGroupChat: () => void;
  refetchMembers: () => void;
}

const GroupChatContext = createContext<GroupChatContextType | null>(null);

export const GroupChatContextWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { session } = useAuthContext();
  const queryClient = useQueryClient();
  const { showToast } = useToastContext();
  const [selectedGroupChatId, setSelectedGroupChatId] = useState<string | null>(
    null
  );

  // Fetch all group chats
  const {
    data: groupChats = [],
    isLoading: isLoadingGroupChats,
    refetch: refetchGroupChats,
  } = useQuery({
    queryFn: getGroupChats,
    queryKey: ["group-chats", session?.user.id],
    enabled: !!session?.user.id,
  });

  // Fetch selected group chat details
  const {
    data: selectedGroupChat = null,
    isLoading: isLoadingGroupChat,
    refetch: refetchGroupChat,
  } = useQuery({
    queryFn: () => getGroupChat(selectedGroupChatId!),
    queryKey: ["group-chat", selectedGroupChatId],
    enabled: !!selectedGroupChatId && !!session?.user.id,
  });

  // Fetch group chat members
  const {
    data: groupChatMembers = [],
    isLoading: isLoadingMembers,
    refetch: refetchMembers,
  } = useQuery({
    queryFn: () => getGroupChatMembers(selectedGroupChatId!),
    queryKey: ["group-chat-members", selectedGroupChatId],
    enabled: !!selectedGroupChatId && !!session?.user.id,
    refetchOnWindowFocus: true,
  });

  // Update group chat name mutation
  const { mutateAsync: updateGroupChatMutation } = useMutation({
    mutationFn: updateGroupChat,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["group-chats"] });
      queryClient.invalidateQueries({
        queryKey: ["group-chat", selectedGroupChatId],
      });
      showToast({
        title: "Success",
        description: "Group chat updated",
        type: "success",
        duration: 3000,
      });
    },
    onError: (error: Error) => {
      console.error("Error updating group chat:", error);
      showToast({
        title: "Error",
        description: "Failed to update group chat",
        type: "error",
        duration: 3000,
      });
    },
  });

  // Add member mutation
  const { mutateAsync: addMemberMutation } = useMutation({
    mutationFn: addMemberToGroupChat,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["group-chat-members", variables.groupChatId],
      });
      showToast({
        title: "Success",
        description: "Member added to group",
        type: "success",
        duration: 2000,
      });
    },
    onError: (error) => {
      console.error("Error adding member:", error);
      showToast({
        title: "Error",
        description: "Failed to add member",
        type: "error",
        duration: 3000,
      });
    },
  });

  // Remove member mutation
  const { mutateAsync: removeMemberMutation } = useMutation({
    mutationFn: removeMemberFromGroupChat,
    onSuccess: () => {
      if (selectedGroupChatId) {
        queryClient.invalidateQueries({
          queryKey: ["group-chat-members", selectedGroupChatId],
        });
      } else {
        queryClient.invalidateQueries({ queryKey: ["group-chat-members"] });
      }
      showToast({
        title: "Success",
        description: "Member removed from group",
        type: "success",
        duration: 2000,
      });
    },
    onError: (error) => {
      console.error("Error removing member:", error);
      showToast({
        title: "Error",
        description: "Failed to remove member",
        type: "error",
        duration: 3000,
      });
    },
  });

  // Actions
  const selectGroupChat = (groupChatId: string) => {
    setSelectedGroupChatId(groupChatId);
  };

  const updateGroupChatName = async (id: string, name: string) => {
    await updateGroupChatMutation({ id, name });
  };

  const addMemberToGroupChatAction = async (
    groupChatId: string,
    contact: RelationshipRow
  ) => {
    await addMemberMutation({ groupChatId, userId: contact.related_user_id });
  };

  const removeMemberFromGroupChatAction = async (memberId: string) => {
    if (!selectedGroupChatId) {
      console.warn("removeMemberFromGroupChatAction_no_group_selected");
      return;
    }
    await removeMemberMutation({
      memberId,
    });
  };

  const value: GroupChatContextType = {
    // Data
    groupChats,
    selectedGroupChat,
    groupChatMembers,

    // Loading states
    isLoadingGroupChats,
    isLoadingGroupChat,
    isLoadingMembers,

    // Actions
    selectGroupChat,
    updateGroupChatName,
    addMemberToGroupChat: addMemberToGroupChatAction,
    removeMemberFromGroupChat: removeMemberFromGroupChatAction,

    // Queries
    refetchGroupChats,
    refetchGroupChat,
    refetchMembers,
  };

  return (
    <GroupChatContext.Provider value={value}>
      {children}
    </GroupChatContext.Provider>
  );
};

export const useGroupChatContext = () => {
  const context = useContext(GroupChatContext);
  if (!context) {
    throw new Error(
      "useGroupChatContext must be used within a GroupChatContextWrapper"
    );
  }
  return context;
};
